import * as THREE from 'three';

const DEFAULT_AMOUNT = 0.06;
const MAX_AMOUNT = 0.08;
const MAX_INFLIGHT = 4;
const ZERO_TILT = Object.freeze({ x: 0, y: 0 });

function readSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem('nm-depth') || 'null');
    const amount = Number(saved?.amt);
    return {
      on: saved?.on !== false,
      amt: Number.isFinite(amount) ? THREE.MathUtils.clamp(amount, 0, MAX_AMOUNT) : DEFAULT_AMOUNT,
    };
  } catch {
    return { on: true, amt: DEFAULT_AMOUNT };
  }
}

function imageSize(tex) {
  const image = tex?.image;
  return { w: image?.width || image?.naturalWidth || 1, h: image?.height || image?.naturalHeight || 1 };
}

/**
 * Owns the optional card-depth index and the bounded, lazy depth-texture queue.
 * The returned object is deliberately renderer-agnostic: buildCard adds its
 * hooks to card objects and each presentation surface declares visibility.
 */
export function createDepthArt({ offline = false } = {}) {
  const settings = readSettings();
  const loader = new THREE.TextureLoader();
  const flatDepth = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1);
  flatDepth.colorSpace = THREE.NoColorSpace;
  flatDepth.needsUpdate = true;

  let ids = null;
  let urlFor = (id) => `./assets/depth/${encodeURIComponent(id)}.png`;
  let inflight = 0;
  let loaded = 0;
  let serial = 0;
  const queue = [];
  const records = new Set();
  const recordsById = new Map();
  const indexListeners = new Set();

  const debug = {
    index: null,
    loaded: 0,
    inflight: 0,
    cards: {},
    inject({ ids: nextIds, url } = {}) {
      if (offline) return 0;
      ids = new Set(Array.isArray(nextIds) ? nextIds.filter((id) => typeof id === 'string') : []);
      debug.index = ids.size;
      if (typeof url === 'function') urlFor = url;
      for (const notify of indexListeners) notify();
      return ids.size;
    },
  };
  // Non-enumerable QA high-water mark; the public debug shape remains the
  // requested {index, loaded, inflight, cards} contract.
  Object.defineProperty(debug, 'peakInflight', { value: 0, writable: true });

  function depthId(data) { return data?.altOf || data?.id || null; }

  function paintDebug(id) {
    if (!id) return;
    const set = recordsById.get(id);
    if (!set?.size) { delete debug.cards[id]; return; }
    const live = [...set].filter((record) => !record.disposed);
    if (!live.length) { delete debug.cards[id]; return; }
    const record = live.find((r) => r.material?.uniforms?.uHasDepth?.value === 1) || live[0];
    const tilt = record.material?.uniforms?.uTilt?.value;
    debug.cards[id] = {
      hasDepth: record.material?.uniforms?.uHasDepth?.value === 1,
      tilt: tilt ? { x: +tilt.x.toFixed(3), y: +tilt.y.toFixed(3) } : { x: 0, y: 0 },
    };
  }

  function configureDepthTexture(tex) {
    tex.colorSpace = THREE.NoColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return tex;
  }

  function finish(record) {
    inflight--;
    debug.inflight = inflight;
    record.loading = false;
    pump();
  }

  function pump() {
    while (inflight < MAX_INFLIGHT && queue.length) {
      const record = queue.shift();
      if (record.disposed || !record.wanted || record.loading || record.loaded) continue;
      record.loading = true;
      const generation = record.generation;
      inflight++;
      debug.inflight = inflight;
      debug.peakInflight = Math.max(debug.peakInflight, inflight);
      loader.load(urlFor(record.id), (tex) => {
        configureDepthTexture(tex);
        if (record.disposed || record.generation !== generation) {
          tex.dispose();
        } else {
          record.material.uniforms.uDepth.value = tex;
          record.material.uniforms.uHasDepth.value = settings.on ? 1 : 0;
          record.loaded = true;
          loaded++;
          debug.loaded = loaded;
          paintDebug(record.debugId);
        }
        finish(record);
      }, undefined, () => { record.failed = true; finish(record); });
    }
  }

  function request(record) {
    if (!record || record.disposed || record.loaded || record.loading || record.queued || record.failed || !settings.on) return;
    record.wanted = true;
    record.queued = true;
    queue.push(record);
    // Clear this before selection; failed indexed assets remain inert for this
    // card instance and are never converted into speculative per-card probes.
    queueMicrotask(() => { record.queued = false; pump(); });
  }

  function disposeRecord(record) {
    if (!record || record.disposed) return;
    record.disposed = true;
    record.wanted = false;
    record.generation++;
    const depth = record.material.uniforms.uDepth.value;
    if (depth !== flatDepth) {
      depth.dispose();
      record.material.uniforms.uDepth.value = flatDepth;
      record.material.uniforms.uHasDepth.value = 0;
      record.loaded = false;
      loaded--;
      debug.loaded = loaded;
    }
    record.material.userData.ownedMap?.dispose();
    record.material.userData.ownedMap = null;
    records.delete(record);
    recordsById.get(record.debugId)?.delete(record);
    paintDebug(record.debugId);
  }

  function syncMap(material, tex) {
    if (!tex) return;
    // v125: the shared art texture keeps its sRGB tag. The GPU decodes it to
    // linear on sample and the chain-end OutputPass encodes once. The old
    // NoColorSpace clone (from before OutputPass owned the encode) skipped the
    // decode, so every depth/alt card rendered ~30% too bright — the "washed
    // out" cards Jack kept seeing (art-only luma 183 vs source 139, 2026-09-06).
    material.userData.ownedMap?.dispose(); material.userData.ownedMap = null;
    material.uniforms.map.value = tex;
    material.uniforms.uRep.value.copy(tex.repeat);
    material.uniforms.uOff.value.copy(tex.offset);
    const { w, h } = imageSize(tex);
    material.uniforms.uShiftScale.value.set(Math.min(1, h / w), Math.min(1, w / h));
  }

  function makeMaterial(tex, data) {
    const id = depthId(data);
    const debugId = data?.id || id;
    const material = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: tex },
        uDepth: { value: flatDepth },
        uHasDepth: { value: 0 },
        uTint: { value: new THREE.Color(1, 1, 1) },
        uRep: { value: tex.repeat.clone() },
        uOff: { value: tex.offset.clone() },
        uTilt: { value: new THREE.Vector2() },
        uAmt: { value: settings.amt },
        uShiftScale: { value: new THREE.Vector2(1, 1) },
      },
      vertexShader: `varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */`
        precision highp float;
        varying vec2 vUv;
        uniform sampler2D map, uDepth;
        uniform float uHasDepth, uAmt;
        uniform vec3 uTint;
        uniform vec2 uRep, uOff, uTilt, uShiftScale;
        void main() {
          vec2 shift = uTilt * uAmt * uHasDepth * uShiftScale;
          float room = 1.0 - length(shift) * 1.15;
          vec2 uv = 0.5 + (vUv - 0.5) * room;
          vec2 p = uv;
          for (int i = 0; i < 8; i++) {
            float z = texture2D(uDepth, p * uRep + uOff).r;
            p = mix(p, uv + (z - 0.5) * shift, 0.5);
          }
          vec3 color = texture2DGradEXT(map, p * uRep + uOff,
            dFdx(vUv * uRep), dFdy(vUv * uRep)).rgb * uTint;
          gl_FragColor = vec4(color, 1.0);
        }`,
    });
    material.extensions.derivatives = true;
    Object.defineProperty(material, 'color', { get() { return material.uniforms.uTint.value; } });
    Object.defineProperty(material, 'map', {
      get() { return material.uniforms.map.value; },
      set(next) { syncMap(material, next); },
    });
    syncMap(material, tex);

    const record = {
      id, debugId, material, disposed: false, wanted: false, queued: false,
      loading: false, loaded: false, failed: false, generation: ++serial,
    };
    records.add(record);
    if (!recordsById.has(debugId)) recordsById.set(debugId, new Set());
    recordsById.get(debugId).add(record);
    material.userData.depthRecord = record;
    const baseDispose = material.dispose.bind(material);
    material.dispose = () => { disposeRecord(record); baseDispose(); };
    paintDebug(debugId);
    return material;
  }

  function trackPlain(material, data) {
    const debugId = data?.id;
    if (!debugId || !material) return material;
    const record = { debugId, material: null, disposed: false };
    if (!recordsById.has(debugId)) recordsById.set(debugId, new Set());
    recordsById.get(debugId).add(record);
    const baseDispose = material.dispose.bind(material);
    material.dispose = () => {
      if (!record.disposed) {
        record.disposed = true;
        recordsById.get(debugId)?.delete(record);
        paintDebug(debugId);
      }
      baseDispose();
    };
    paintDebug(debugId);
    return material;
  }

  function update(card, group, motion = ZERO_TILT) {
    const material = card?.depthMat;
    const record = material?.userData?.depthRecord;
    if (!record || record.disposed) return;
    request(record);
    const gx = Number(motion?.x) || 0;
    const gy = Number(motion?.y) || 0;
    const x = THREE.MathUtils.clamp(group.rotation.y * 1.6 + gy * 2.4, -1, 1);
    const y = THREE.MathUtils.clamp(-group.rotation.x * 1.6 - gx * 2.4, -1, 1);
    material.uniforms.uTilt.value.set(x, y);
    paintDebug(record.debugId);
  }

  function setSettings(next) {
    if (typeof next?.on === 'boolean') settings.on = next.on;
    if (Number.isFinite(Number(next?.amt))) settings.amt = THREE.MathUtils.clamp(Number(next.amt), 0, MAX_AMOUNT);
    try { localStorage.setItem('nm-depth', JSON.stringify(settings)); } catch { /* storage may be unavailable */ }
    for (const record of records) {
      record.material.uniforms.uAmt.value = settings.amt;
      record.material.uniforms.uHasDepth.value = settings.on && record.loaded ? 1 : 0;
      if (settings.on && record.wanted && !record.loaded) request(record);
      paintDebug(record.debugId);
    }
    pump();
  }

  const ready = (async () => {
    if (offline) return;
    try {
      const response = await fetch('./assets/depth/index.json', { signal: AbortSignal.timeout(2500) });
      if (!response.ok) return;
      const index = await response.json();
      if (index?.v !== 1 || !Array.isArray(index.ids)) return;
      ids = new Set(index.ids.filter((id) => typeof id === 'string'));
      debug.index = ids.size;
    } catch { /* 404/offline/invalid index: depth remains absent everywhere */ }
  })();

  return {
    ready, debug, settings, maxAmount: MAX_AMOUNT, setSettings,
    has(data) { const id = depthId(data); return !!id && !!ids?.has(id); },
    makeMaterial, trackPlain,
    update,
    onIndexChange(fn) { indexListeners.add(fn); return () => indexListeners.delete(fn); },
  };
}
